<template>
  <div>
    <header-image
      v-if="provider && (provider.img_header || provider.img_header === undefined)"
      :src="(provider.img_header !== undefined ? provider.img_header : provider.img) || ''"
    />

      <div v-if="provider === null" class="pa-5 text-center">
        <v-progress-circular indeterminate color="secondary"/>
      </div>

      <div v-else-if="provider !== null && typeof provider === 'object' && !provider.url" style="width: 100%;">
        <v-container>
          <lang-switcher :langs="provider.langs"/>

          <content-root :contents="provider.contents"></content-root>

          <v-container class="mt-8 mb-16 pa-0">
            <v-row>
              <v-col v-for="tour in tours" :key="tour.slug" cols="12" sm="6" md="4">
                <image-card :href="'/' + tour.slug" :src="tour.img" :title="tour.title" :desc="tour.desc"/>
              </v-col>
            </v-row>
          </v-container>

          <div v-if="provider && provider.map" class="my-16">
            <l-map
              ref="map"
              :zoom="zoom"
              :center="center"
              class="map"
              @ready="onMapReady"
            >
              <l-tile-layer
                :url="mapLayer.url"
                :attribution="mapLayer.attribution"
              />

              <l-marker
                v-for="tour in mapTours"
                :key="tour.id"
                :latLng="[tour.startPos.lat, tour.startPos.lng]"
                :zIndexOffset="100"
              >
                <l-popup>
                  <image-card :href="'/' + tour.slug" :src="tour.img" :title="tour.title" :desc="tour.desc" class="map-card"/>
                </l-popup>
              </l-marker>
            </l-map>
          </div>
        </v-container>

        <v-sheet color="primary" class="white--text text-caption text-center pa-5">
          <author-info :provider="provider"/>
          <div class="pt-3">
            <a v-if="impressumLink" :href="impressumLink" target="_blank">{{ $t('imprint') }}</a>
            <a v-else @click.stop="$store.commit('showImprintDlg', true)">{{ $t('imprint') }}</a>
          </div>
        </v-sheet>
      </div>

      <div v-else-if="provider === 'notfound'">
        <v-container class="text-center">
          <div class="pa-10">{{ $t('err_provider_not_found') }}</div>
          <v-btn to="/">{{ $t('overview') }}</v-btn>
        </v-container>
      </div>

      <div v-else-if="provider === 'error'">
        <v-container class="text-center">
          <div class="pa-10">{{ $t('err_oops') }}</div>
          <v-btn to="/">{{ $t('overview') }}</v-btn>
        </v-container>
      </div>
  </div>
</template>

<script>
import ContentRoot from '@/components/content/ContentRoot.vue'
import HeaderImage from '@/components/HeaderImage.vue'
import ImageCard from '@/components/ImageCard.vue'
import LangSwitcher from '@/components/LangSwitcher.vue'
import AuthorInfo from '@/components/AuthorInfo.vue'

import { LMap, LTileLayer, LMarker, LPopup } from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'

// marker icon fix
import { Icon } from 'leaflet'
delete Icon.Default.prototype._getIconUrl
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

const LAYERS = {
  openstreetmap: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
  },
  swisstopo_pixelkarte_farbe: {
    url: 'https://wmts20.geo.admin.ch/1.0.0/ch.swisstopo.pixelkarte-farbe/default/current/3857/{z}/{x}/{y}.jpeg',
    attribution: '&copy; <a href="https://www.swisstopo.admin.ch/" target="_blank">SwissTopo</a>'
  },
  swisstopo_swissimage: {
    url: 'https://wmts20.geo.admin.ch/1.0.0/ch.swisstopo.swissimage/default/current/3857/{z}/{x}/{y}.jpeg',
    attribution: '&copy; <a href="https://www.swisstopo.admin.ch/" target="_blank">SwissTopo</a>'
  }
}

const FIT_BOUNDS_PADDING = 80
const SWISS_CENTER = [46.8182, 8.2275]
const SWISS_ZOOM = 8

export default {
  name: 'Provider',
  components: {
    ContentRoot,
    HeaderImage,
    ImageCard,
    LangSwitcher,
    AuthorInfo,
    LMap,
    LTileLayer,
    LMarker,
    LPopup
  },
  data () {
    return {
      center: SWISS_CENTER,
      zoom: SWISS_ZOOM
    }
  },
  computed: {
    provider () {
      return this.$store.state.provider
    },
    tours () {
      return this.provider ? this.provider.tours : []
    },
    locale () {
      return this.$root.$i18n.locale
    },
    lang () {
      return this.$route.query.lang || this.locale
    },
    mapLayer () {
      if (!this.provider?.map) return null
      return LAYERS[this.provider.map]
    },
    mapTours () {
      return this.tours.filter(tour => tour.startPos)
    },
    impressumLink () {
      return this.provider?.wl_link_imprint
    }
  },
  methods: {
    load () {
      const slug = this.$route.params.provider.toLowerCase()
      this.$store.dispatch('loadProvider', {
        slug: slug,
        lang: this.lang
      })
    },
    onMapReady () {
      this.map = this.$refs.map.mapObject
      if (this.mapTours.length > 1) {
        // fit map to all tours
        const positions = []
        this.mapTours.forEach(tour => {
          positions.push([tour.startPos.lat, tour.startPos.lng])
        })
        this.map.fitBounds(positions, { padding: [FIT_BOUNDS_PADDING, FIT_BOUNDS_PADDING] })
      }
    }
  },
  watch: {
    lang () {
      this.load()
    },
    provider (provider) {
      if (provider && provider.url) {
        window.location.href = provider.url
      }
    }
  },
  created () {
    this.load()
  }
}
</script>

<style scoped>
.map {
  aspect-ratio: 16 / 9;
  max-width: 100%;
  min-height: 360px;
  z-index: 0;
}
.map-card {
  width: 300px;
  height: 200px;
}
</style>